import React, { useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { withHeaderMessages } from '../higherOrderComponents/WithHeaderMessages';
import { Row, Col, Container, Alert } from 'react-bootstrap';
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from '../lib/components/LoadingSpinner';

const Reports = (props) => {

    const navigate = useNavigate();

    const initialState = {
        reports: [],
        onDemandReports: [],
        error: '',
        isLoading: true,
        isOnDemandLoading: true,
        user: {},
		isProfileLoading: true,
        isReportError: false,
        isOnDemandError: false
    };

    const reducer = (state, action) => {
        switch (action.type) {
            case 'reports':
                return {
                    ...state,
                    reports: action.payload,
                    isLoading: false
                }
            case 'onDemandReports':
                return {
                    ...state,
                    onDemandReports: action.payload,
                    isOnDemandLoading: false
                }
            case 'error':
                return {
                    ...state,
                    error: action.payload
                }
            case 'isLoading':
                return {
                    ...state,
                    isLoading: action.payload
                }
            case 'isOnDemandLoading':
                return {
                    ...state,
                    isOnDemandLoading: action.payload
                }
            case 'hideLoader':
                return {
                    ...state,
                    isLoading: false
                }
            case 'isReportError':
                return {
                    ...state,
                    isReportError: action.payload
                }
            case 'isOnDemandError':
                return {
                    ...state,
                    isOnDemandError: false
                }
			case "user":
				return {
					...state,
					user: action.payload,
				}
			case "isProfileLoading":
				return {
					...state,
					isProfileLoading: action.payload,
				}
            default:
                throw new Error();
        }
    }

    const [reportState, dispatch] = useReducer(reducer, initialState);
    const download = async (url, referenceId, event) => {
        dispatch({ type: 'error', payload: '' })
        await props.api.getBlob(`/${url}/${referenceId}`)
            .then(response => {
                if (response.status === 200) {
                    var filename = "";
                    var disposition = response.headers['content-disposition'];
                    if (disposition && disposition.indexOf('attachment') !== -1) {
                        var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                        var matches = filenameRegex.exec(disposition);
                        if (matches != null && matches[1]) {
                            filename = matches[1].replace(/['"]/g, '');
                        }
                    }

                    return { filename: filename, blob: new Blob([response.data], { type: response.data.type }) };
                }
            })
            .then(file => {
                let blobUrl = window.URL.createObjectURL(file.blob);

                let anchor = document.createElement('a');
                anchor.href = blobUrl;
                anchor.download = file.filename;
                document.body.appendChild(anchor);
                anchor.click();
                setTimeout(function () {
                    document.body.removeChild(anchor);
                    window.URL.revokeObjectURL(blobUrl);
                }, 100);
            })
            .catch(error => {
                dispatch({ type: 'error', payload: 'Error retrieving report. Please try again later or contact support if the problem persists.' });
            });
    }
    const downloadFile = async (referenceId, event) => {
        await download('api/report', referenceId, event);
    }

    const downloadOnDemandFile = async (referenceId, event) => {
        await download('api/on-demand-report', referenceId, event);
    }

    const distinctPeriods = () => {
        const { reports, onDemandReports } = reportState;
        const allDates = reports
            .map(r => r.periodEnd)
            .concat(onDemandReports.map(r => r.requestDate))
            .map(d => new Date(d))
            .map(d => `${d.getFullYear()}${String(d.getMonth()).padStart(2, '0')}`);

        const output = allDates.filter((a, b) => allDates.indexOf(a) === b);
        return output;
    }

    const requestReport = () => {
        navigate("/on-demand-report-request");
    }

    useEffect(() => {
        async function getReports() {
            props.header.setHeader("reports");
            await props.api.get(`/api/report/list`)
                .then(response => {
                    if (response.status === 200) {
                        return response.data;
                    }
                })
                .then(data => {
                    dispatch({ type: 'reports', payload: data });
                })
                .catch(() => {
                    dispatch({ type: 'isLoading', payload: false })
                    dispatch({ type: 'isReportError', payload: true })
                })
                .finally(() => {
                    dispatch({ type: 'isLoading', payload: false })
                });
        }
        getReports();
    }, [])

    useEffect(() => {
        async function getReports() {
            await props.api.get(`/api/on-demand-report/list`)
                .then(response => {
                    if (response.status === 200) {
                        return response.data;
                    }
                })
                .then(data => {
                    dispatch({ type: 'onDemandReports', payload: data });
                })
                .catch(() => {
                    dispatch({ type: 'isOnDemandLoading', payload: false })
                    dispatch({ type: 'isOnDemandError', payload: true })
                })
                .finally(() => {
                    dispatch({ type: 'isOnDemandLoading', payload: false })
                });
        }
        getReports();
    }, [])

    useEffect(() => {
		async function getProfile() {
			await props.api
				.get("/api/user")
				.then((response) => {
					if (response.status === 200) {
						return response.data;
					}
				})
				.then((data) => {
					dispatch({ type: "user", payload: data });
				})
				.then(dispatch({ type: "isProfileLoading", payload: false }))
				.catch(dispatch({ type: "isProfileLoading", payload: false }));
		}
		getProfile();
	}, []);

    const { reports, onDemandReports, isReportError, isOnDemandError, user, error } = reportState;
    const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    return (
        <Container>
            {reportState.isLoading || reportState.isOnDemandLoading
                ? <LoadingSpinner />
                : <div>
                    {error !== '' &&
                        <Alert bsStyle="danger" role="alert">
                            {error}
                        </Alert>
                    }
                    {isReportError === false && isOnDemandError === false &&
                        <div>
                            <Row>
                                <Col sm={12}>
                                    <h4 className="me-15 text-start mt-2">Reports</h4>
                                </Col>
                            </Row>
                            {(user.onDemandReports) &&
                                <Row>
                                    <Col sm={12}>
                                        <div className="mt-2">
                                            <button className="btn btn-sec" onClick={requestReport}>Request On Demand Report</button>
                                        </div>
                                    </Col>
                                </Row>
                            }
                            {distinctPeriods().sort((a, b) => b - a).map(pe => {
                                const month = Number(pe.substring(4));
                                const year = Number(pe.substring(0, 4));
                                return (
                                    <Container key={pe}>
                                        <Row>
                                            <Col sm={12} className='my-2'>
                                                <h4>{monthNames[month]} {year}</h4>
                                            </Col>
                                        </Row>
                                        {reports
                                            .filter(r => {
                                                const d = new Date(r.periodEnd);
                                                return `${d.getFullYear()}${String(d.getMonth()).padStart(2, '0')}` === pe
                                            })
                                            .sort(function (a, b) {
                                                var nameA = a.customerOrProviderCode.toUpperCase();
                                                var nameB = b.customerOrProviderCode.toUpperCase();
                                                if (nameA < nameB) {
                                                    return -1;
                                                }
                                                if (nameA > nameB) {
                                                    return 1;
                                                }

                                                return 0;
                                            })
                                            .map(r => {
                                                return (
                                                    <Row key={r.referenceId}>
                                                        <Col sm={12} className='mb-3'>
                                                            <span>
                                                                {r.period} {r.friendlyName}
                                                                {(r.forProvider)
                                                                    ? ` for Provider ${r.customerOrProviderCode}`
                                                                    : ` for Customer ${r.customerOrProviderCode}`}
                                                                {(r.additionalParameters)
                                                                    ? ` (${r.additionalParameters})`
                                                                    : ""}
                                                                {(r.sortField)
                                                                    ? ` (Sorted by: ${r.sortField})`
                                                                    : ""}
                                                            </span>
                                                            <button className='mx-3 download-link'
                                                                onClick={(event) => downloadFile(r.referenceId, event)}>
                                                                Download
                                                            </button>
                                                        </Col>
                                                    </Row>
                                                )
                                            })}

                                            {onDemandReports
                                            .filter(r => {
                                                const d = new Date(r.requestDate);
                                                return `${d.getFullYear()}${String(d.getMonth()).padStart(2, '0')}` === pe
                                            })
                                            .sort(function (a, b) {
                                                if (b.requestDate > a.requestDate) { return 1; }
                                                if (b.requestDate < a.requestDate) { return -1; }
                                                return 0;
                                            })
                                            .map(r => {
                                                return (
                                                    <Row key={r.referenceId}>
                                                        <Col sm={12} className='mb-3'>
                                                            <span>
                                                                On Demand {r.friendlyName}
                                                                {(r.providerCode)
                                                                    ? ` for Provider ${r.providerCode}`
                                                                    : r.customerCode
                                                                        ? ` for Customer ${r.customerCode}`
                                                                        : "" }
                                                                {(r.status !== 'Success')
                                                                    ? ` (${r.status})`
                                                                    : ""}
                                                            </span>
                                                            {(r.status === 'Success') &&
                                                                <button className='mx-3 download-link'
                                                                    onClick={(event) => downloadOnDemandFile(r.referenceId, event)}>
                                                                    Download
                                                                </button>
                                                            }
                                                        </Col>
                                                    </Row>
                                                )
                                            })}
                                    </Container>
                                )
                            })
                            }
                        </div>
                    }
                </div>
            }
        </Container>
    );
}

export default withHeaderMessages(WithApiAccess(Reports));